import {useAccessToken, useIsBankOperationsUser, useIsBankUser, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findOrgs, Org} from "../../resources/org"
import {Bank, findBanks} from "../../resources/bank"
import {AsyncResult} from "../../types/asyncResult"
import Loading from "../../components/Loading/Loading"
import React, {useEffect, useRef, useState} from "react"
import {MainSection} from "../../containers/MainSection/MainSection"
import {
	WalletTerms,
	findActiveAssociatedAccounts,
	findActiveAccountsByType,
	getWalletTerms,
	Status,
	updateWalletTerms,
} from "../../resources/walletTerms"
import {Attributes, WalletTermsData} from "./Attributes"
import {useParams} from "react-router"
import TitleBar, {TitleBarAction} from "../../components/TitleBar/TitleBar"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import {EditMode, ErrorDocument} from "../../resources/common"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useToasts} from "react-toast-notifications"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {BankRouting, findBankRoutings} from "../../resources/bankRouting"
import {
	findTransactionMonitoringSettings,
	TransactionMonitoringSettings,
} from "../../resources/transactionMonitoringSettings"
import {Bin, findBins} from "../../resources/bin"
import {useNavigate} from "react-router-dom"
import {isNil} from "lodash"
import {ActionButton, ActionItem} from "../../components/ActionButton/ActionButton"
import Icon from "../../components/Icon/Icon"
import {hasPermission} from "../../services/permission"
import {Formik, FormikValues} from "formik"
import {focusFirstInvalidField} from "../../components/Address/Address"
import * as Yup from "yup"
import General from "./WalletTermsSections/General"
import Limits from "./WalletTermsSections/Limits"
import Clearing from "./WalletTermsSections/Clearing"
import CardSettings from "./WalletTermsSections/CardSettings"
import AccountFees from "./WalletTermsSections/AccountFees"
import Overdraft from "./WalletTermsSections/Overdraft"
import TableOfContent from "../../components/TableOfContent/TableOfContent"
import classNames from "classnames"
import {useModal} from "react-modal-hook"
import ReactTooltip from "react-tooltip"
import BodyPortal from "../../components/BodyPortal/BodyPortal"
import {ToggleWalletTermsModal} from "../../components/WalletsTerms/ToggleWalletTermsModal"
import {findInterestSettings, InterestSettings} from "../../resources/interestSettings"
import {AuditLogEventsHeaderMetaItemProps} from "../../components/AuditLogEventsHeader/AuditLogEventsHeader"
import AuditLogActionButton from "../../components/AuditLogActionButton/AuditLogActionButton"
import AuditLogDrawer from "../../components/AuditLogDrawer/AuditLogDrawer"
import Interest from "./WalletTermsSections/Interest"
import RealTimePayment from "./WalletTermsSections/RealTimePayment"
import {ok} from "neverthrow"
import {AccountType, OrgFBO, OrgGeneralLedgerAccount} from "../../resources/account"

export interface DuplicateWalletTermsState {
	duplicateWalletTerms?: WalletTermsData
}

function Footer({
	state,
	isDisabled,
}: {
	state: AsyncResultIdleRequestState<WalletTerms, ErrorDocument>
	isDisabled: boolean
}) {
	return (
		<div className="card-content">
			{state.match(
				() => (
					<button className="button is-success" disabled={isDisabled}>
						Update
					</button>
				),
				() => (
					<button className="button is-success is-loading">Updating</button>
				),
				(_) => null,
				(err) => (
					<>
						<div className="has-text-danger">{err.errors[0].title}</div>
						<button className="button is-success">Update</button>
					</>
				)
			)}
		</div>
	)
}

type Props = {
	walletTerms: WalletTerms
	orgs: Array<Org>
	orgGlAccounts: Array<OrgGeneralLedgerAccount>
	orgFboAccounts: Array<OrgFBO>
	banks: Array<Bank>
	bankRoutings: Array<BankRouting>
	refresh: () => void
	transactionMonitoringSettings: Array<TransactionMonitoringSettings>
	bins: Array<Bin>
	hasAssociatedAccounts: boolean
	interestSettings: Array<InterestSettings>
}

function Page({
	walletTerms,
	orgs,
	orgGlAccounts,
	orgFboAccounts,
	banks,
	bankRoutings,
	refresh,
	transactionMonitoringSettings,
	bins,
	hasAssociatedAccounts,
	interestSettings,
}: Props) {
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const isBankOperationsUser = useIsBankOperationsUser()
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const navigate = useNavigate()

	const canUpdateWalletTerms = hasPermission("walletTerms", "update") || isUnitUser || isBankOperationsUser
	const [state, update] = useAsyncResultIdle(updateWalletTerms)
	const [isEditing, setIsEditing] = useState(false)
	const [name, setName] = useState(walletTerms.attributes.name)
	const [orgId, setOrgId] = useState(walletTerms.relationships?.org?.data.id)
	const [orgGlId, setOrgGlId] = useState<string>(walletTerms.relationships?.orgGLAccountId?.data.id ?? "")
	const [orgFboId, setOrgFboId] = useState<string>(walletTerms.relationships?.orgFBOAccountId?.data.id ?? "")
	const [minimumIdentityVerificationLevel, setMinimumIdentityVerificationLevel] = useState<string>(
		walletTerms.attributes.minimumIdentityVerificationLevel
	)
	const [transactionMonitoringSettingsId, setTransactionMonitoringSettingsId] = useState(
		walletTerms.relationships?.transactionMonitoringSettings?.data.id
	)
	const [incomingACHFee, setIncomingAchFee] = useState(walletTerms.attributes.incomingACHFee)
	const [outgoingACHFee, setOutgoingAchFee] = useState(walletTerms.attributes.outgoingACHFee)
	const [dailyACHDebitLimit, setDailyAchDebitLimit] = useState(walletTerms.attributes.achLimits.dailyDebit)
	const [dailyACHCreditLimit, setDailyAchCreditLimit] = useState(walletTerms.attributes.achLimits.dailyCredit)
	const [monthlyACHDebitLimit, setMonthlyAchDebitLimit] = useState(walletTerms.attributes.achLimits.monthlyDebit)
	const [monthlyACHCreditLimit, setMonthlyAchCreditLimit] = useState(walletTerms.attributes.achLimits.monthlyCredit)
	const [dailyACHDebitSoftLimit, setDailyAchDebitSoftLimit] = useState(walletTerms.attributes.achLimits.dailyDebitSoft)
	const [monthlyACHDebitSoftLimit, setMonthlyAchDebitSoftLimit] = useState(
		walletTerms.attributes.achLimits.monthlyDebitSoft
	)

	const [dailyCardWithdrawalLimit, setDailyCardWithdrawalLimit] = useState(
		walletTerms.attributes.dailyCardWithdrawalLimit
	)
	const [dailyCardDepositLimit, setDailyCardDepositLimit] = useState(walletTerms.attributes.dailyCardDepositLimit)
	const [dailyCardPurchaseLimit, setDailyCardPurchaseLimit] = useState(walletTerms.attributes.dailyCardPurchaseLimit)
	const [dailyCardTransactionLimit, setDailyCardTransactionLimit] = useState(
		walletTerms.attributes.dailyCardTransactionLimit
	)
	const [achDebitClearingDays, setACHDebitClearingDays] = useState(walletTerms.attributes.achDebitClearingDays)
	const [isAutoCloseEnabled, setIsAutoCloseEnabled] = useState<boolean | undefined>(
		walletTerms.attributes.isAutoCloseEnabled
	)
	const [autoCloseAfter, setAutoCloseAfter] = useState(walletTerms.attributes.autoCloseAfter)

	const [interestTerms] = useState(walletTerms.attributes.interestTerms)
	const [orgInterestShare, setOrgInterestShare] = useState<number | undefined>(interestTerms?.orgShare)
	const [customerInterestShare, setCustomerInterestShare] = useState<number | undefined>(interestTerms?.customerShare)
	const [sponsoredInterestFormula, setSetSponsoredInterestFormula] = useState<string | undefined>(
		interestTerms?.sponsoredInterestFormula
	)

	const [cardProduct] = useState(walletTerms.attributes.cardProduct)
	const [cardQualifier, setCardQualifier] = useState<string | null>(cardProduct ? cardProduct.cardQualifier : null)
	const [individualBinId, setIndividualBinId] = useState<string | null>(
		cardProduct?.individualBinId ? cardProduct.individualBinId : null
	)
	const [businessBinId, setBusinessBinId] = useState<string | null>(
		cardProduct?.businessBinId ? cardProduct.businessBinId : null
	)
	const [limitGroup, setLimitGroup] = useState<string | null>(cardProduct ? cardProduct.limitGroup : null)
	const [maxNumberOfPhysicalCards, setMaxNumberOfPhysicalCards] = useState<number | null>(
		cardProduct ? cardProduct.maxNumberOfPhysicalCards : null
	)
	const [maxNumberOfVirtualCards, setMaxNumberOfVirtualCards] = useState<number | null>(
		cardProduct ? cardProduct.maxNumberOfVirtualCards : null
	)
	const [programmaticPurchaseAuthorizationEnabled, setProgrammaticPurchaseAuthorizationEnabled] = useState<
		boolean | null
	>(cardProduct ? cardProduct.programmaticPurchaseAuthorizationEnabled : null)
	const [programmaticPurchaseAuthorizationTimeoutStrategy, setProgrammaticPurchaseAuthorizationTimeoutStrategy] =
		useState<string | null>(
			cardProduct?.programmaticPurchaseAuthorizationTimeoutStrategy
				? cardProduct.programmaticPurchaseAuthorizationTimeoutStrategy
				: null
		)
	const [programmaticPurchaseAuthorizationIncludeCredit, setProgrammaticPurchaseAuthorizationIncludeCredit] = useState<
		boolean | null
	>(
		cardProduct?.programmaticPurchaseAuthorizationIncludeCredit
			? cardProduct.programmaticPurchaseAuthorizationIncludeCredit
			: null
	)
	const [programmaticCardTransactionAuthorizationEnabled, setProgrammaticCardTransactionAuthorizationEnabled] =
		useState<boolean | null>(cardProduct ? cardProduct.programmaticCardTransactionAuthorizationEnabled : null)
	const [
		programmaticCardTransactionAuthorizationTimeoutStrategy,
		setProgrammaticCardTransactionAuthorizationTimeoutStrategy,
	] = useState<string | null>(
		cardProduct?.programmaticCardTransactionAuthorizationTimeoutStrategy
			? cardProduct.programmaticCardTransactionAuthorizationTimeoutStrategy
			: null
	)
	const [
		programmaticCardTransactionAuthorizationIncludeCredit,
		setProgrammaticCardTransactionAuthorizationIncludeCredit,
	] = useState<boolean | null>(
		cardProduct?.programmaticCardTransactionAuthorizationIncludeCredit
			? cardProduct.programmaticCardTransactionAuthorizationIncludeCredit
			: null
	)
	const [programmaticAtmAuthorizationEnabled, setProgrammaticAtmAuthorizationEnabled] = useState<boolean | null>(
		cardProduct ? cardProduct.programmaticAtmAuthorizationEnabled : null
	)
	const [programmaticAtmAuthorizationTimeoutStrategy, setProgrammaticAtmAuthorizationTimeoutStrategy] = useState<
		string | null
	>(
		cardProduct?.programmaticAtmAuthorizationTimeoutStrategy
			? cardProduct.programmaticAtmAuthorizationTimeoutStrategy
			: null
	)

	const [interestSettingsId, setInterestSettingsId] = useState(
		walletTerms.relationships?.interestSettings?.data.id ? walletTerms.relationships?.interestSettings?.data.id : null
	)

	const [isOverrideInterestSettingsId, setIsOverrideInterestSettingsId] = useState(!!interestSettingsId)

	const [programmaticAdviceAuthorizationEnabled, setProgrammaticAdviceAuthorizationEnabled] = useState<boolean | null>(
		cardProduct?.programmaticAdviceAuthorizationEnabled ? cardProduct.programmaticAdviceAuthorizationEnabled : null
	)

	const [incomingWireFee, setIncomingWireFee] = useState(walletTerms.attributes.incomingWireFee)
	const [outgoingWireFee, setOutgoingWireFee] = useState(walletTerms.attributes.outgoingWireFee)
	const [checkDepositClearingDays, setCheckDepositClearingDays] = useState(
		walletTerms.attributes.checkDepositClearingDays
	)

	const [checkDepositMaxClearingDays, setCheckDepositMaxClearingDays] = useState<number | undefined | null>(
		walletTerms.attributes.checkDepositMaxClearingDays ?? null
	)
	const [checkDepositMinClearingDays, setCheckDepositMinClearingDays] = useState<number | undefined | null>(
		walletTerms.attributes.checkDepositMinClearingDays ?? null
	)

	const [dailyCheckDepositLimit, setDailyCheckDepositLimit] = useState(walletTerms.attributes.checkDepositLimits.daily)
	const [monthlyCheckDepositLimit, setMonthlyCheckDepositLimit] = useState(
		walletTerms.attributes.checkDepositLimits.monthly
	)
	const [dailyCheckDepositSoftLimit, setDailyCheckDepositSoftLimit] = useState(
		walletTerms.attributes.checkDepositLimits.dailySoft
	)
	const [monthlyCheckDepositSoftLimit, setMonthlyCheckDepositSoftLimit] = useState(
		walletTerms.attributes.checkDepositLimits.monthlySoft
	)

	const [dailyWireLimit, setDailyWireLimit] = useState(walletTerms.attributes.wireLimits.daily)
	const [monthlyWireLimit, setMonthlyWireLimit] = useState(walletTerms.attributes.wireLimits.monthly)
	const [dailyWireSoftLimit, setDailyWireSoftLimit] = useState(walletTerms.attributes.wireLimits.dailySoft)
	const [monthlyWireSoftLimit, setMonthlyWireSoftLimit] = useState(walletTerms.attributes.wireLimits.monthlySoft)

	const [dailyCheckPaymentLimit, setDailyCheckPaymentLimit] = useState<number | undefined | null>(
		walletTerms.attributes.checkPaymentLimits.daily != undefined
			? walletTerms.attributes.checkPaymentLimits.daily
			: undefined
	)
	const [monthlyCheckPaymentLimit, setMonthlyCheckPaymentLimit] = useState<number | undefined | null>(
		walletTerms.attributes.checkPaymentLimits.monthly ?? undefined
	)
	const [dailyCheckPaymentSoftLimit, setDailyCheckPaymentSoftLimit] = useState<number | undefined | null>(
		walletTerms.attributes.checkPaymentLimits.dailySoft ?? undefined
	)
	const [monthlyCheckPaymentSoftLimit, setMonthlyCheckPaymentSoftLimit] = useState<number | undefined | null>(
		walletTerms.attributes.checkPaymentLimits.monthlySoft ?? undefined
	)

	const [enableOverdraft, setEnableOverdraft] = useState(walletTerms.attributes.overdraftSettings?.enableOverdraft)
	const [overdraftDaysLimit, setOverdraftDaysLimit] = useState(
		walletTerms.attributes.overdraftSettings?.overdraftDaysLimit ?? null
	)

	const [enableCustomerInterestFormula, setEnableCustomerInterestFormula] = useState(
		walletTerms.attributes.enableCustomerInterestFormula
	)
	const [customerInterestFormula, setCustomerInterestFormula] = useState(
		walletTerms.attributes.customerInterestFormula ?? undefined
	)

	const [checkWritingEnabled, setCheckWritingEnabled] = useState(walletTerms.attributes.checkWritingEnabled ?? false)
	const [checkWritingOriginationEnabled, setCheckWritingOriginationEnabled] = useState(
		walletTerms.attributes.checkWritingOriginationEnabled ?? false
	)
	const [realTimePaymentsEnabled, setRealTimePaymentsEnabled] = useState(
		walletTerms.attributes.realTimePaymentsEnabled ?? false
	)

	const [additionalVerificationThreshold, setAdditionalVerificationThreshold] = useState<number | undefined | null>(
		walletTerms.attributes.additionalVerificationThreshold ?? undefined
	)

	const [bankId, setBankId] = useState(walletTerms.relationships?.bank?.data.id)
	const [bankRoutingId, setBankRoutingId] = useState(walletTerms.relationships?.bankRouting?.data.id)

	const [enableCheckDepositDynamicClearingDays, setEnableCheckDepositDynamicClearingDays] = useState(
		walletTerms.attributes.enableCheckDepositDynamicClearingDays
	)
	const [enableDynamicClearingDaysAchDebit, setEnableDynamicClearingDaysAchDebit] = useState(
		walletTerms.attributes.enableDynamicClearingDaysAchDebit
	)

	const [achDebitMinClearingDays, setAchDebitMinClearingDays] = useState<number | undefined | null>(
		walletTerms.attributes.achDebitMinClearingDays ?? null
	)
	const [achDebitMaxClearingDays, setAchDebitMaxClearingDays] = useState<number | undefined | null>(
		walletTerms.attributes.achDebitMaxClearingDays ?? null
	)
	const formRef = useRef<HTMLFormElement>(null)
	const [isEnabled] = useState(walletTerms.attributes.status == Status.Enabled)

	const [showToggleWalletTermsModal, hideToggleWalletTerms] = useModal(() => (
		<ToggleWalletTermsModal
			walletTerms={walletTerms}
			disable={isEnabled}
			close={hideToggleWalletTerms}
			onSuccess={refresh}
		/>
	))

	const [isAchDebitFraudPredictEnabled, setIsAchDebitFraudPredictEnabled] = useState(
		walletTerms.attributes.isAchDebitFraudPredictEnabled
	)

	const [isSweepEnabled, setIsSweepEnabled] = useState(walletTerms.attributes.isSweepEnabled)
	const [orgInternationalServiceFee, setOrgInternationalServiceFee] = useState<number | null>(
		walletTerms.attributes.cardProduct?.orgInternationalServiceFee ?? 0
	)

	const [isAuditLogDrawerOpened, setIsAuditLogDrawerOpened] = useState<boolean>(false)
	useEffect(() => {
		if (state.isOk()) {
			addToast("Wallet Terms Updated Successfully", {appearance: "success"})
			setIsEditing(false)
			refresh()
		}
	}, [state])

	const submit = () => {
		const anyCardProduct = individualBinId != null || businessBinId != null

		const cardProduct =
			cardQualifier != null &&
			limitGroup != null &&
			maxNumberOfPhysicalCards != null &&
			maxNumberOfVirtualCards != null &&
			(!programmaticPurchaseAuthorizationEnabled || programmaticPurchaseAuthorizationTimeoutStrategy != null) &&
			(!programmaticCardTransactionAuthorizationEnabled ||
				programmaticCardTransactionAuthorizationTimeoutStrategy != null) &&
			anyCardProduct
				? {
						cardQualifier: cardQualifier,
						limitGroup: limitGroup,
						maxNumberOfPhysicalCards: maxNumberOfPhysicalCards,
						maxNumberOfVirtualCards: maxNumberOfVirtualCards,
						programmaticPurchaseAuthorizationEnabled: !!programmaticPurchaseAuthorizationEnabled,
						programmaticPurchaseAuthorizationTimeoutStrategy: programmaticPurchaseAuthorizationTimeoutStrategy,
						programmaticPurchaseAuthorizationIncludeCredit: !!programmaticPurchaseAuthorizationIncludeCredit,
						programmaticCardTransactionAuthorizationEnabled: !!programmaticCardTransactionAuthorizationEnabled,
						programmaticCardTransactionAuthorizationTimeoutStrategy:
							programmaticCardTransactionAuthorizationTimeoutStrategy,
						programmaticCardTransactionAuthorizationIncludeCredit:
							!!programmaticCardTransactionAuthorizationIncludeCredit,
						programmaticAtmAuthorizationEnabled: !!programmaticAtmAuthorizationEnabled,
						programmaticAtmAuthorizationTimeoutStrategy: programmaticAtmAuthorizationTimeoutStrategy,
						programmaticAdviceAuthorizationEnabled: !!programmaticAdviceAuthorizationEnabled,
						individualBinId: individualBinId,
						businessBinId: businessBinId,
						orgInternationalServiceFee: orgInternationalServiceFee,
				  }
				: null

		const interestTerms = {
			orgShare: orgInterestShare,
			customerShare: customerInterestShare,
			sponsoredInterestFormula: sponsoredInterestFormula,
		}

		const overdraftSettings = {
			enableOverdraft,
			overdraftDaysLimit,
		}

		const editedAttributes = isUnitUser
			? {
					incomingACHFee,
					outgoingACHFee,
					dailyACHDebitLimit,
					dailyACHCreditLimit,
					monthlyACHDebitLimit,
					monthlyACHCreditLimit,
					dailyACHDebitSoftLimit,
					monthlyACHDebitSoftLimit,
					dailyCardWithdrawalLimit,
					dailyCardDepositLimit,
					dailyCardPurchaseLimit,
					dailyCardTransactionLimit,
					achDebitClearingDays,
					incomingWireFee,
					outgoingWireFee,
					checkDepositClearingDays,
					checkDepositMinClearingDays,
					checkDepositMaxClearingDays,
					dailyCheckDepositLimit,
					monthlyCheckDepositLimit,
					dailyCheckDepositSoftLimit,
					monthlyCheckDepositSoftLimit,
					isAutoCloseEnabled,
					autoCloseAfter,
					overdraftSettings,
					dailyWireLimit,
					monthlyWireLimit,
					dailyWireSoftLimit,
					monthlyWireSoftLimit,
					dailyCheckPaymentLimit,
					monthlyCheckPaymentLimit,
					dailyCheckPaymentSoftLimit,
					monthlyCheckPaymentSoftLimit,
					checkWritingEnabled,
					enableCheckDepositDynamicClearingDays,
					enableDynamicClearingDaysAchDebit,
					achDebitMaxClearingDays,
					achDebitMinClearingDays,
					isAchDebitFraudPredictEnabled,
					isSweepEnabled,
					checkWritingOriginationEnabled,
					realTimePaymentsEnabled,
					additionalVerificationThreshold,
					enableCustomerInterestFormula,
					customerInterestFormula,
			  }
			: {
					incomingACHFee,
					outgoingACHFee,
					dailyACHDebitLimit,
					dailyACHCreditLimit,
					monthlyACHDebitLimit,
					monthlyACHCreditLimit,
					dailyACHDebitSoftLimit,
					monthlyACHDebitSoftLimit,
					dailyCardWithdrawalLimit,
					dailyCardDepositLimit,
					dailyCardPurchaseLimit,
					dailyCardTransactionLimit,
					achDebitClearingDays,
					incomingWireFee,
					outgoingWireFee,
					checkDepositClearingDays,
					checkDepositMinClearingDays,
					checkDepositMaxClearingDays,
					dailyCheckDepositLimit,
					monthlyCheckDepositLimit,
					dailyCheckDepositSoftLimit,
					monthlyCheckDepositSoftLimit,
					isAutoCloseEnabled,
					autoCloseAfter,
					dailyWireLimit,
					monthlyWireLimit,
					dailyWireSoftLimit,
					monthlyWireSoftLimit,
					dailyCheckPaymentLimit,
					monthlyCheckPaymentLimit,
					dailyCheckPaymentSoftLimit,
					monthlyCheckPaymentSoftLimit,
					checkWritingEnabled,
					enableCheckDepositDynamicClearingDays,
					enableDynamicClearingDaysAchDebit,
					achDebitMaxClearingDays,
					achDebitMinClearingDays,
					isAchDebitFraudPredictEnabled,
					isSweepEnabled,
					checkWritingOriginationEnabled,
					realTimePaymentsEnabled,
					additionalVerificationThreshold,
					enableCustomerInterestFormula,
					customerInterestFormula,
			  }

		const editedRelationships = {
			transactionMonitoringSettingsId,
			interestSettingsId,
		}
		update(isUnitUser, accessToken, walletTerms, cardProduct, interestTerms, editedAttributes, editedRelationships)
	}

	function validate() {
		const anyCardProduct = individualBinId != null || businessBinId != null
		const deleteCardProduct = [
			individualBinId,
			businessBinId,
			cardQualifier,
			limitGroup,
			maxNumberOfPhysicalCards,
			maxNumberOfVirtualCards,
			programmaticPurchaseAuthorizationEnabled,
			programmaticPurchaseAuthorizationTimeoutStrategy,
			programmaticCardTransactionAuthorizationEnabled,
			programmaticCardTransactionAuthorizationTimeoutStrategy,
			programmaticAtmAuthorizationEnabled,
			programmaticAtmAuthorizationTimeoutStrategy,
			programmaticPurchaseAuthorizationIncludeCredit,
			programmaticCardTransactionAuthorizationIncludeCredit,
			programmaticAdviceAuthorizationEnabled,
		].every((i) => i == null)

		if (isUnitUser) {
			return (
				(cardQualifier != null &&
					limitGroup != null &&
					maxNumberOfPhysicalCards != null &&
					maxNumberOfVirtualCards != null &&
					(programmaticPurchaseAuthorizationEnabled
						? !isNil(programmaticPurchaseAuthorizationTimeoutStrategy || null)
						: true) &&
					(programmaticCardTransactionAuthorizationEnabled
						? !isNil(programmaticCardTransactionAuthorizationTimeoutStrategy || null)
						: true) &&
					(programmaticAtmAuthorizationEnabled ? !isNil(programmaticAtmAuthorizationTimeoutStrategy || null) : true) &&
					anyCardProduct) ||
				deleteCardProduct
			)
		} else if (isBankOperationsUser) {
			return maxNumberOfPhysicalCards != null && maxNumberOfVirtualCards != null
		} else {
			return false
		}
	}

	function createDuplicateState(): DuplicateWalletTermsState | undefined {
		if (
			bankId !== undefined &&
			bankRoutingId !== undefined &&
			customerInterestShare !== undefined &&
			orgId !== undefined &&
			orgInterestShare !== undefined &&
			transactionMonitoringSettingsId !== undefined &&
			isAutoCloseEnabled !== undefined &&
			autoCloseAfter !== undefined &&
			enableOverdraft !== undefined &&
			checkWritingEnabled !== undefined &&
			isAchDebitFraudPredictEnabled !== undefined &&
			isSweepEnabled !== undefined &&
			checkWritingOriginationEnabled !== undefined &&
			realTimePaymentsEnabled !== undefined
		) {
			return {
				duplicateWalletTerms: {
					name,
					orgGlId,
					orgFboId,
					minimumIdentityVerificationLevel,
					achDebitClearingDays,
					bankId,
					bankRoutingId,
					businessBinId,
					cardQualifier,
					checkDepositClearingDays,
					checkDepositMinClearingDays,
					checkDepositMaxClearingDays,
					customerInterestShare,
					dailyACHCreditLimit,
					dailyACHDebitLimit,
					dailyACHDebitSoftLimit,
					dailyCardDepositLimit,
					dailyCardPurchaseLimit,
					dailyCardTransactionLimit,
					dailyCardWithdrawalLimit,
					dailyCheckDepositLimit,
					dailyCheckDepositSoftLimit,
					incomingACHFee,
					incomingWireFee,
					outgoingWireFee,
					individualBinId,
					limitGroup,
					maxNumberOfPhysicalCards,
					maxNumberOfVirtualCards,
					monthlyACHCreditLimit,
					monthlyACHDebitLimit,
					monthlyACHDebitSoftLimit,
					monthlyCheckDepositLimit,
					monthlyCheckDepositSoftLimit,
					orgId,
					orgInterestShare,
					outgoingACHFee,
					programmaticPurchaseAuthorizationEnabled,
					programmaticPurchaseAuthorizationTimeoutStrategy,
					programmaticPurchaseAuthorizationIncludeCredit,
					programmaticCardTransactionAuthorizationEnabled,
					programmaticCardTransactionAuthorizationTimeoutStrategy,
					programmaticCardTransactionAuthorizationIncludeCredit,
					transactionMonitoringSettingsId,
					sponsoredInterestFormula,
					programmaticAtmAuthorizationEnabled,
					programmaticAtmAuthorizationTimeoutStrategy,
					programmaticAdviceAuthorizationEnabled,
					isAutoCloseEnabled,
					autoCloseAfter,
					enableOverdraft,
					overdraftDaysLimit,
					dailyWireLimit,
					dailyWireSoftLimit,
					monthlyWireLimit,
					monthlyWireSoftLimit,
					dailyCheckPaymentLimit,
					monthlyCheckPaymentLimit,
					dailyCheckPaymentSoftLimit,
					monthlyCheckPaymentSoftLimit,
					checkWritingEnabled,
					interestSettingsId,
					enableCheckDepositDynamicClearingDays,
					enableDynamicClearingDaysAchDebit,
					achDebitMaxClearingDays,
					achDebitMinClearingDays,
					isAchDebitFraudPredictEnabled,
					isSweepEnabled,
					checkWritingOriginationEnabled,
					realTimePaymentsEnabled,
					orgInternationalServiceFee,
					additionalVerificationThreshold,
					enableCustomerInterestFormula,
					customerInterestFormula,
				},
			}
		}
	}

	// const link = new Link<number | undefined>(5)

	function calcMaxAllocation(x: number, y: number) {
		return 100 - x - y
	}

	const initialFormValues = {
		name: walletTerms.attributes.name ?? undefined,
		orgId: walletTerms.relationships?.org?.data.id ?? undefined,
		bankId: walletTerms.relationships?.bank?.data.id ?? undefined,
		bankRoutingId: walletTerms.relationships?.bankRouting?.data.id ?? undefined,
		orgInterestShare: walletTerms.attributes.interestTerms?.orgShare ?? undefined,
		customerInterestShare: walletTerms.attributes.interestTerms?.customerShare ?? undefined,
		sponsoredInterestFormula: undefined,
		transactionMonitoringSettingsId: walletTerms.relationships?.transactionMonitoringSettings?.data.id ?? undefined,
		isAutoCloseEnabled: walletTerms.attributes.isAutoCloseEnabled ?? undefined,
		autoCloseAfter: walletTerms.attributes.autoCloseAfter ?? undefined,
		incomingACHFee: walletTerms.attributes.incomingACHFee ?? undefined,
		outgoingACHFee: walletTerms.attributes.outgoingACHFee ?? undefined,
		achDebitClearingDays: walletTerms.attributes.achDebitClearingDays ?? undefined,
		dailyACHDebitLimit: walletTerms.attributes.achLimits.dailyDebit ?? undefined,
		dailyACHCreditLimit: walletTerms.attributes.achLimits.dailyCredit ?? undefined,
		dailyACHDebitSoftLimit: walletTerms.attributes.achLimits.dailyDebitSoft ?? undefined,
		monthlyACHDebitLimit: walletTerms.attributes.achLimits.monthlyDebit ?? undefined,
		monthlyACHCreditLimit: walletTerms.attributes.achLimits.monthlyCredit ?? undefined,
		monthlyACHDebitSoftLimit: walletTerms.attributes.achLimits.monthlyDebitSoft ?? undefined,
		dailyCardWithdrawalLimit: walletTerms.attributes.dailyCardWithdrawalLimit ?? undefined,
		dailyCardDepositLimit: walletTerms.attributes.dailyCardDepositLimit ?? undefined,
		dailyCardPurchaseLimit: walletTerms.attributes.dailyCardPurchaseLimit ?? undefined,
		dailyCardTransactionLimit: walletTerms.attributes.dailyCardTransactionLimit ?? undefined,
		dailyCheckDepositLimit: walletTerms.attributes.checkDepositLimits.daily ?? undefined,
		dailyCheckDepositSoftLimit: walletTerms.attributes.checkDepositLimits.dailySoft ?? undefined,
		monthlyCheckDepositLimit: walletTerms.attributes.checkDepositLimits.monthly ?? undefined,
		monthlyCheckDepositSoftLimit: walletTerms.attributes.checkDepositLimits.monthlySoft ?? undefined,
		incomingWireFee: walletTerms.attributes.incomingWireFee ?? undefined,
		outgoingWireFee: walletTerms.attributes.outgoingWireFee ?? undefined,
		individualBinId: cardProduct?.individualBinId ? cardProduct.individualBinId : null ?? undefined,
		businessBinId: cardProduct?.businessBinId ? cardProduct.businessBinId : null ?? undefined,
		cardQualifier: cardProduct ? cardProduct.cardQualifier : null ?? undefined,
		limitGroup: cardProduct ? cardProduct.limitGroup : null ?? undefined,
		maxNumberOfPhysicalCards: cardProduct ? cardProduct.maxNumberOfPhysicalCards : null ?? undefined,
		maxNumberOfVirtualCards: cardProduct ? cardProduct.maxNumberOfVirtualCards : null ?? undefined,
		dailyWireLimit: walletTerms.attributes.wireLimits.daily ?? undefined,
		dailyWireSoftLimit: walletTerms.attributes.wireLimits.dailySoft ?? undefined,
		monthlyWireLimit: walletTerms.attributes.wireLimits.monthly ?? undefined,
		monthlyWireSoftLimit: walletTerms.attributes.wireLimits.monthlySoft ?? undefined,
		dailyCheckPaymentLimit: walletTerms.attributes.checkPaymentLimits.daily ?? undefined,
		dailyCheckPaymentSoftLimit: walletTerms.attributes.checkPaymentLimits.dailySoft ?? undefined,
		monthlyCheckPaymentLimit: walletTerms.attributes.checkPaymentLimits.monthly ?? undefined,
		monthlyCheckPaymentSoftLimit: walletTerms.attributes.checkPaymentLimits.monthlySoft ?? undefined,
		programmaticPurchaseAuthorizationEnabled: cardProduct
			? cardProduct.programmaticPurchaseAuthorizationEnabled
			: null ?? undefined,
		programmaticPurchaseAuthorizationTimeoutStrategy: cardProduct?.programmaticPurchaseAuthorizationTimeoutStrategy
			? cardProduct.programmaticPurchaseAuthorizationTimeoutStrategy
			: null ?? undefined,
		programmaticPurchaseAuthorizationIncludeCredit: cardProduct
			? cardProduct.programmaticPurchaseAuthorizationIncludeCredit
			: null ?? undefined,
		programmaticCardTransactionAuthorizationEnabled: cardProduct
			? cardProduct.programmaticCardTransactionAuthorizationEnabled
			: null ?? undefined,
		programmaticCardTransactionAuthorizationTimeoutStrategy:
			cardProduct?.programmaticCardTransactionAuthorizationTimeoutStrategy
				? cardProduct.programmaticCardTransactionAuthorizationTimeoutStrategy
				: null ?? undefined,
		programmaticCardTransactionAuthorizationIncludeCredit: cardProduct
			? cardProduct.programmaticCardTransactionAuthorizationIncludeCredit
			: null ?? undefined,
		programmaticAtmAuthorizationEnabled: cardProduct
			? cardProduct.programmaticAtmAuthorizationEnabled
			: null ?? undefined,
		programmaticAtmAuthorizationTimeoutStrategy: cardProduct?.programmaticAtmAuthorizationTimeoutStrategy
			? cardProduct.programmaticAtmAuthorizationTimeoutStrategy
			: null ?? undefined,
		programmaticAdviceAuthorizationEnabled: cardProduct
			? cardProduct.programmaticAdviceAuthorizationEnabled
			: null ?? undefined,
		checkDepositClearingDays: walletTerms.attributes.checkDepositClearingDays ?? undefined,
		checkDepositMinClearingDays: walletTerms.attributes.checkDepositMinClearingDays ?? undefined,
		checkDepositMaxClearingDays: walletTerms.attributes.checkDepositMaxClearingDays ?? undefined,
		enableOverdraft: walletTerms.attributes.overdraftSettings?.enableOverdraft ?? undefined,
		overdraftDaysLimit: walletTerms.attributes.overdraftSettings?.overdraftDaysLimit ?? null ?? undefined,
		checkWritingEnabled: walletTerms.attributes.checkWritingEnabled ?? undefined,
		isOverrideInterestSettingsId: undefined,
		interestSettingsId: walletTerms.relationships?.interestSettings?.data.id ?? undefined,
		enableCheckDepositDynamicClearingDays: walletTerms.attributes.enableCheckDepositDynamicClearingDays ?? undefined,
		enableDynamicClearingDaysAchDebit: walletTerms.attributes.enableDynamicClearingDaysAchDebit ?? undefined,
		achDebitMinClearingDays: walletTerms.attributes.achDebitMinClearingDays ?? undefined,
		achDebitMaxClearingDays: walletTerms.attributes.achDebitMaxClearingDays ?? undefined,
		isAchDebitFraudPredictEnabled: walletTerms.attributes.isAchDebitFraudPredictEnabled ?? undefined,
		isSweepEnabled: walletTerms.attributes.isSweepEnabled ?? undefined,
		checkWritingOriginationEnabled: walletTerms.attributes.checkWritingOriginationEnabled ?? undefined,
		realTimePaymentsEnabled: walletTerms.attributes.realTimePaymentsEnabled ?? undefined,
		additionalVerificationThreshold: walletTerms.attributes.additionalVerificationThreshold ?? undefined,
		enableCustomerInterestFormula: walletTerms.attributes.enableCustomerInterestFormula ?? undefined,
		customerInterestFormula: walletTerms.attributes.customerInterestFormula ?? undefined,
		interestFormula: walletTerms.attributes.interestTerms?.interestFormula ?? undefined,
		interestRate: walletTerms.attributes.interestTerms?.interestRate ?? undefined,
	}

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(""),
		orgId: Yup.string(),
		bankId: Yup.string(),
		bankRoutingId: Yup.string(),
		orgInterestShare: Yup.number()
			.min(0, "Error")
			.test({
				name: "max",
				exclusive: false,
				params: {},
				message: "Error",
				test: function (value: any) {
					return value <= calcMaxAllocation(this.parent.customerInterestShare, this.parent.unitInterestShare)
				},
			})
			.required(""),
		customerInterestShare: Yup.number()
			.min(0, "Error")
			.test({
				name: "max",
				exclusive: false,
				params: {},
				message: "Error",
				test: function (value: any) {
					return value <= calcMaxAllocation(this.parent.orgInterestShare, this.parent.unitInterestShare)
				},
			})
			.required(""),
		unitInterestShare: Yup.number()
			.min(0, "Error")
			.test({
				name: "max",
				exclusive: false,
				params: {},
				message: "Error",
				test: function (value: any) {
					return value <= calcMaxAllocation(this.parent.customerInterestShare, this.parent.customerInterestShare)
				},
			})
			.required(""),
		sponsoredInterestFormula: Yup.number().nullable().min(0, ""),
		transactionMonitoringSettingsId: Yup.string().required(""),
		isAutoCloseEnabled: Yup.boolean(),
		autoCloseAfter: Yup.number().min(0, ""),
		incomingACHFee: Yup.number().required(""),
		outgoingACHFee: Yup.number().required(""),
		achDebitClearingDays: Yup.string(),
		dailyACHDebitLimit: Yup.number().required(""),
		dailyACHCreditLimit: Yup.number().required(""),
		dailyACHDebitSoftLimit: Yup.number().required(""),
		monthlyACHDebitLimit: Yup.number().required(""),
		monthlyACHCreditLimit: Yup.number().required(""),
		monthlyACHDebitSoftLimit: Yup.number().required(""),
		dailyCardWithdrawalLimit: Yup.number().required(""),
		dailyCardDepositLimit: Yup.number().required(""),
		dailyCardPurchaseLimit: Yup.number().required(""),
		dailyCardTransactionLimit: Yup.number().required(""),
		dailyCheckDepositLimit: Yup.number().required(""),
		dailyCheckDepositSoftLimit: Yup.number().required(""),
		monthlyCheckDepositLimit: Yup.number().required(""),
		monthlyCheckDepositSoftLimit: Yup.number().required(""),
		incomingWireFee: Yup.number().required(""),
		outgoingWireFee: Yup.number().required(""),
		individualBinId: Yup.string(),
		businessBinId: Yup.string(),
		cardQualifier: Yup.string(),
		limitGroup: Yup.string(),
		maxNumberOfPhysicalCards: Yup.number(),
		maxNumberOfVirtualCards: Yup.number(),
		programmaticPurchaseAuthorizationEnabled: Yup.boolean(),
		programmaticPurchaseAuthorizationTimeoutStrategy: Yup.string(),
		programmaticPurchaseAuthorizationIncludeCredit: Yup.boolean(),
		programmaticCardTransactionAuthorizationEnabled: Yup.boolean(),
		programmaticCardTransactionAuthorizationTimeoutStrategy: Yup.string(),
		programmaticCardTransactionAuthorizationIncludeCredit: Yup.boolean(),
		programmaticAtmAuthorizationEnabled: Yup.boolean(),
		programmaticAtmAuthorizationTimeoutStrategy: Yup.string(),
		programmaticAdviceAuthorizationEnabled: Yup.boolean(),
		checkDepositClearingDays: Yup.string(),
		enableOverdraft: Yup.boolean(),
		overdraftDaysLimit: Yup.number().min(0, ""),
		checkWritingEnabled: Yup.boolean().required(""),
		isOverrideInterestSettingsId: Yup.boolean(),
		isAchDebitFraudPredictEnabled: Yup.boolean(),
		isSweepEnabled: Yup.boolean(),
		checkWritingOriginationEnabled: Yup.boolean().required(""),
		realTimePaymentsEnabled: Yup.boolean().required(""),
	})

	const submitForm = (values: FormikValues) => {
		const anyCardProduct = values.individualBinId != null || values.businessBinId != null

		const cardProduct =
			values.cardQualifier != null &&
			values.limitGroup != null &&
			values.maxNumberOfPhysicalCards != null &&
			values.maxNumberOfVirtualCards != null &&
			(!values.programmaticPurchaseAuthorizationEnabled ||
				values.programmaticPurchaseAuthorizationTimeoutStrategy != null) &&
			(!values.programmaticCardTransactionAuthorizationEnabled ||
				values.programmaticCardTransactionAuthorizationTimeoutStrategy != null) &&
			(!values.programmaticAtmAuthorizationEnabled || values.programmaticAtmAuthorizationTimeoutStrategy != null) &&
			!values.programmaticAdviceAuthorizationEnabled &&
			anyCardProduct
				? {
						cardQualifier: values.cardQualifier,
						limitGroup: values.limitGroup,
						maxNumberOfPhysicalCards: values.maxNumberOfPhysicalCards,
						maxNumberOfVirtualCards: values.maxNumberOfVirtualCards,
						programmaticPurchaseAuthorizationEnabled: !!values.programmaticPurchaseAuthorizationEnabled,
						programmaticPurchaseAuthorizationTimeoutStrategy: values.programmaticPurchaseAuthorizationTimeoutStrategy,
						programmaticPurchaseAuthorizationIncludeCredit: !!values.programmaticPurchaseAuthorizationIncludeCredit,
						programmaticCardTransactionAuthorizationEnabled: !!values.programmaticCardTransactionAuthorizationEnabled,
						programmaticCardTransactionAuthorizationTimeoutStrategy:
							values.programmaticCardTransactionAuthorizationTimeoutStrategy,
						programmaticCardTransactionAuthorizationIncludeCredit:
							!!values.programmaticCardTransactionAuthorizationIncludeCredit,
						programmaticAtmAuthorizationEnabled: !!values.programmaticAtmAuthorizationEnabled,
						programmaticAtmAuthorizationTimeoutStrategy: values.programmaticAtmAuthorizationTimeoutStrategy,
						programmaticAdviceAuthorizationEnabled: !!values.programmaticAdviceAuthorizationEnabled,
						individualBinId: values.individualBinId,
						businessBinId: values.businessBinId,
				  }
				: null

		const interestTerms = {
			orgShare: values.orgInterestShare,
			customerShare: values.customerInterestShare,
			unitShare: values.unitInterestShare,
			sponsoredInterestFormula: values.sponsoredInterestFormula,
		}

		const overdraftSettings = {
			enableOverdraft: values.enableOverdraft,
			overdraftDaysLimit: values.overdraftDaysLimit,
		}

		const editedAttributes = {
			incomingACHFee: values.incomingACHFee,
			outgoingACHFee: values.outgoingACHFee,
			dailyACHDebitLimit: values.dailyACHDebitLimit,
			dailyACHCreditLimit: values.dailyACHCreditLimit,
			monthlyACHDebitLimit: values.monthlyACHDebitLimit,
			monthlyACHCreditLimit: values.monthlyACHCreditLimit,
			dailyACHDebitSoftLimit: values.dailyACHDebitSoftLimit,
			monthlyACHDebitSoftLimit: values.monthlyACHDebitSoftLimit,
			dailyCardWithdrawalLimit: values.dailyCardWithdrawalLimit,
			dailyCardDepositLimit: values.dailyCardDepositLimit,
			dailyCardPurchaseLimit: values.dailyCardPurchaseLimit,
			dailyCardTransactionLimit: values.dailyCardTransactionLimit,
			achDebitClearingDays: values.achDebitClearingDays,
			incomingWireFee: values.incomingWireFee,
			outgoingWireFee: values.outgoingWireFee,
			checkDepositClearingDays: values.checkDepositClearingDays,
			checkDepositMinClearingDays: values.checkDepositMinClearingDays,
			checkDepositMaxClearingDays: values.checkDepositMaxClearingDays,
			dailyCheckDepositLimit: values.dailyCheckDepositLimit,
			monthlyCheckDepositLimit: values.monthlyCheckDepositLimit,
			dailyCheckDepositSoftLimit: values.dailyCheckDepositSoftLimit,
			monthlyCheckDepositSoftLimit: values.monthlyCheckDepositSoftLimit,
			dailyCheckPaymentLimit: values.dailyCheckPaymentLimit,
			monthlyCheckPaymentLimit: values.monthlyCheckPaymentLimit,
			dailyCheckPaymentSoftLimit: values.dailyCheckPaymentSoftLimit,
			monthlyCheckPaymentSoftLimit: values.monthlyCheckPaymentSoftLimit,
			isAutoCloseEnabled: values.isAutoCloseEnabled,
			autoCloseAfter: values.autoCloseAfter,
			overdraftSettings,
			checkWritingEnabled: values.checkWritingEnabled,
			enableCheckDepositDynamicClearingDays: values.enableCheckDepositDynamicClearingDays,
			enableDynamicClearingPeriod: values.enableDynamicClearingPeriod,
			achDebitMaxClearingDays: values.achDebitMaxClearingDays,
			achDebitMinClearingDays: values.achDebitMinClearingDays,
			isAchDebitFraudPredictEnabled: values.isAchDebitFraudPredictEnabled,
			isSweepEnabled: values.isSweepEnabled,
			checkWritingOriginationEnabled: values.checkWritingOriginationEnabled,
			realTimePaymentsEnabled: values.realTimePaymentsEnabled,
		}

		const editedRelationships = {
			transactionMonitoringSettingsId: values.transactionMonitoringSettingsId,
			interestSettingsId: values.interestSettingsId, // Test it when starting to use the Formik
		}

		update(isUnitUser, accessToken, walletTerms, cardProduct, interestTerms, editedAttributes, editedRelationships)
	}

	const auditLogEventsHeaderMetaItems: AuditLogEventsHeaderMetaItemProps[] = [
		{
			text: walletTerms.attributes.name,
			icon: "list-bullet-point-square",
			tooltipText: "Wallet Terms",
		},
		{
			text: banks.find((bank) => bank.id === bankId)?.attributes.name,
			icon: "bank",
			tooltipText: "Bank Name",
		},
		{
			text: orgs.find((org) => org.id === orgId)?.attributes.name,
			icon: "user-hierachy2",
			tooltipText: "Org Name",
		},
	]

	return (
		<>
			<TitleBar
				title={walletTerms.attributes.name}
				breadcrumbs={[{text: "Wallets Terms", path: "/wallet-terms/"}]}
				meta={
					canUpdateWalletTerms
						? [
								{text: `#${bankId}`, icon: "bank"},
								{text: orgs.find((org) => org.id === orgId)?.attributes.name ?? "", icon: "user-hierachy2"},
						  ]
						: undefined
				}
			>
				{(isUnitUser || isBankUser) && <AuditLogActionButton onClick={() => setIsAuditLogDrawerOpened(true)} />}
				{canUpdateWalletTerms ? (
					<>
						{!isEditing ? (
							!isEnabled ? (
								<div data-tip={true} data-iscapture={true} data-for={"cannot-edit-when-disabled"}>
									<button className="button page-title-button is-static disabled">
										<Icon icon={"pencil-1--interface-essential"} size={16} />
										<span>{"Edit"}</span>
									</button>
								</div>
							) : (
								<button className="button page-title-button" onClick={() => setIsEditing(true)}>
									<Icon icon={"pencil-1--interface-essential"} size={16} />
									<span>{"Edit"}</span>
								</button>
							)
						) : (
							<>
								<button className="button button-cancel" onClick={() => refresh()}>
									<Icon icon={"interface-delete-interface-essential"} size={12} />
									<span>{"Discard"}</span>
								</button>
							</>
						)}

						<TitleBarAction>
							<ActionButton enableActions={true}>
								<ActionItem
									onClick={() => navigate("/wallet-terms/new", {state: createDuplicateState()})}
									title={"Duplicate"}
									icon={"doc-layer-copy"}
								/>
								<ActionItem
									data-tip
									disabled={hasAssociatedAccounts}
									data-iscapture={true}
									data-for={hasAssociatedAccounts && "disable-walletTerms"}
									title={isEnabled ? "Disable" : "Enable"}
									icon={isEnabled ? "disable-delete--interface-essential" : "approve-v-check-validation"}
									onClick={showToggleWalletTermsModal}
								/>
							</ActionButton>
						</TitleBarAction>
					</>
				) : null}
			</TitleBar>

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={() => {
						setIsAuditLogDrawerOpened(false)
					}}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="wallet-terms"
					resourceId={walletTerms.id}
				/>
				<div className={classNames(!canUpdateWalletTerms && "wallet-terms")}>
					{!canUpdateWalletTerms ? (
						<>
							<Formik
								initialValues={initialFormValues}
								validateOnMount={true}
								validationSchema={validationSchema}
								onSubmit={(values) => submitForm(values)}
							>
								{({values, handleSubmit, handleChange, setFieldValue, errors, isValid, handleBlur}) => (
									<form
										className="wallet-terms-form"
										noValidate
										ref={formRef}
										onSubmit={(e) => {
											e.preventDefault()
											handleSubmit(e)
											if (!isValid) {
												focusFirstInvalidField(errors)
											}
										}}
									>
										{isEditing && (
											<General
												editMode={EditMode.EDIT}
												values={values}
												onChange={handleChange}
												setFieldValue={setFieldValue}
												onBlur={handleBlur}
												transactionMonitoringSettings={transactionMonitoringSettings}
												banks={banks}
												bankRoutings={bankRoutings}
											/>
										)}
										<Limits
											editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
										/>
										<Clearing
											editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
										/>
										<CardSettings
											editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
											bins={bins}
										/>
										<AccountFees
											editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
										/>
										<Interest
											editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
										/>
										<RealTimePayment
											editMode={EditMode.VIEW}
											values={values}
											onChange={handleChange}
											setFieldValue={setFieldValue}
											onBlur={handleBlur}
										/>
										{isEditing && (
											<Overdraft
												editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
												values={values}
												onChange={handleChange}
												setFieldValue={setFieldValue}
												onBlur={handleBlur}
											/>
										)}
									</form>
								)}
							</Formik>
							<TableOfContent querySelector=".wallet-terms-card-title .card-title" />
						</>
					) : (
						<form
							onSubmit={(e) => {
								e.preventDefault()
								submit()
							}}
						>
							<fieldset disabled={!isEditing}>
								<Attributes
									editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
									orgs={orgs}
									banks={banks}
									orgGlAccounts={orgGlAccounts}
									orgFboAccounts={orgFboAccounts}
									bankRoutings={bankRoutings}
									transactionMonitoringSettings={transactionMonitoringSettings}
									bins={bins}
									name={name}
									setName={setName}
									orgId={orgId as string}
									setOrgId={setOrgId as React.Dispatch<React.SetStateAction<string>>}
									bankId={bankId as string}
									setBankId={setBankId as React.Dispatch<React.SetStateAction<string>>}
									bankRoutingId={bankRoutingId as string}
									setBankRoutingId={setBankRoutingId as React.Dispatch<React.SetStateAction<string>>}
									transactionMonitoringSettingsId={transactionMonitoringSettingsId as string}
									setTransactionMonitoringSettingsId={
										setTransactionMonitoringSettingsId as React.Dispatch<React.SetStateAction<string>>
									}
									incomingACHFee={incomingACHFee}
									setIncomingAchFee={setIncomingAchFee}
									outgoingACHFee={outgoingACHFee}
									setOutgoingAchFee={setOutgoingAchFee}
									dailyACHDebitLimit={dailyACHDebitLimit}
									setDailyAchDebitLimit={setDailyAchDebitLimit}
									dailyACHCreditLimit={dailyACHCreditLimit}
									setDailyAchCreditLimit={setDailyAchCreditLimit}
									monthlyACHDebitLimit={monthlyACHDebitLimit}
									setMonthlyAchDebitLimit={setMonthlyAchDebitLimit}
									monthlyACHCreditLimit={monthlyACHCreditLimit}
									setMonthlyAchCreditLimit={setMonthlyAchCreditLimit}
									dailyACHDebitSoftLimit={dailyACHDebitSoftLimit}
									setDailyAchDebitSoftLimit={setDailyAchDebitSoftLimit}
									monthlyACHDebitSoftLimit={monthlyACHDebitSoftLimit}
									setMonthlyAchDebitSoftLimit={setMonthlyAchDebitSoftLimit}
									dailyCardWithdrawalLimit={dailyCardWithdrawalLimit}
									setDailyCardWithdrawalLimit={setDailyCardWithdrawalLimit}
									dailyCardDepositLimit={dailyCardDepositLimit}
									setDailyCardDepositLimit={setDailyCardDepositLimit}
									dailyCardPurchaseLimit={dailyCardPurchaseLimit}
									setDailyCardPurchaseLimit={setDailyCardPurchaseLimit}
									dailyCardTransactionLimit={dailyCardTransactionLimit}
									setDailyCardTransactionLimit={setDailyCardTransactionLimit}
									achDebitClearingDays={achDebitClearingDays}
									setACHDebitClearingDays={setACHDebitClearingDays}
									individualBinId={individualBinId}
									setIndividualBinId={setIndividualBinId}
									businessBinId={businessBinId}
									setBusinessBinId={setBusinessBinId}
									cardQualifier={cardQualifier}
									setCardQualifier={setCardQualifier}
									limitGroup={limitGroup}
									setLimitGroup={setLimitGroup}
									interestSettings={interestSettings}
									maxNumberOfPhysicalCards={maxNumberOfPhysicalCards}
									setMaxNumberOfPhysicalCards={setMaxNumberOfPhysicalCards}
									maxNumberOfVirtualCards={maxNumberOfVirtualCards}
									setMaxNumberOfVirtualCards={setMaxNumberOfVirtualCards}
									orgInterestShare={orgInterestShare as number}
									setOrgInterestShare={setOrgInterestShare as React.Dispatch<React.SetStateAction<number>>}
									customerInterestShare={customerInterestShare as number}
									setCustomerInterestShare={setCustomerInterestShare as React.Dispatch<React.SetStateAction<number>>}
									sponsoredInterestFormula={sponsoredInterestFormula}
									setSponsoredInterestFormula={
										setSetSponsoredInterestFormula as React.Dispatch<React.SetStateAction<string | undefined>>
									}
									incomingWireFee={incomingWireFee}
									setIncomingWireFee={setIncomingWireFee}
									outgoingWireFee={outgoingWireFee}
									setOutgoingWireFee={setOutgoingWireFee}
									programmaticPurchaseAuthorizationEnabled={programmaticPurchaseAuthorizationEnabled}
									setProgrammaticPurchaseAuthorizationEnabled={setProgrammaticPurchaseAuthorizationEnabled}
									programmaticPurchaseAuthorizationTimeoutStrategy={programmaticPurchaseAuthorizationTimeoutStrategy}
									setProgrammaticPurchaseAuthorizationTimeoutStrategy={
										setProgrammaticPurchaseAuthorizationTimeoutStrategy
									}
									programmaticPurchaseAuthorizationIncludeCredit={programmaticPurchaseAuthorizationIncludeCredit}
									setProgrammaticPurchaseAuthorizationIncludeCredit={setProgrammaticPurchaseAuthorizationIncludeCredit}
									programmaticCardTransactionAuthorizationEnabled={programmaticCardTransactionAuthorizationEnabled}
									setProgrammaticCardTransactionAuthorizationEnabled={
										setProgrammaticCardTransactionAuthorizationEnabled
									}
									programmaticCardTransactionAuthorizationTimeoutStrategy={
										programmaticCardTransactionAuthorizationTimeoutStrategy
									}
									setProgrammaticCardTransactionAuthorizationTimeoutStrategy={
										setProgrammaticCardTransactionAuthorizationTimeoutStrategy
									}
									programmaticCardTransactionAuthorizationIncludeCredit={
										programmaticCardTransactionAuthorizationIncludeCredit
									}
									setProgrammaticCardTransactionAuthorizationIncludeCredit={
										setProgrammaticCardTransactionAuthorizationIncludeCredit
									}
									programmaticAtmAuthorizationEnabled={programmaticAtmAuthorizationEnabled}
									setProgrammaticAtmAuthorizationEnabled={setProgrammaticAtmAuthorizationEnabled}
									programmaticAtmAuthorizationTimeoutStrategy={programmaticAtmAuthorizationTimeoutStrategy}
									setProgrammaticAtmAuthorizationTimeoutStrategy={setProgrammaticAtmAuthorizationTimeoutStrategy}
									programmaticAdviceAuthorizationEnabled={programmaticAdviceAuthorizationEnabled}
									setProgrammaticAdviceAuthorizationEnabled={setProgrammaticAdviceAuthorizationEnabled}
									checkDepositClearingDays={checkDepositClearingDays}
									setCheckDepositClearingDays={setCheckDepositClearingDays}
									checkDepositMinClearingDays={checkDepositMinClearingDays}
									setCheckDepositMinClearingDays={setCheckDepositMinClearingDays}
									checkDepositMaxClearingDays={checkDepositMaxClearingDays}
									setCheckDepositMaxClearingDays={setCheckDepositMaxClearingDays}
									dailyCheckDepositLimit={dailyCheckDepositLimit}
									setDailyCheckDepositLimit={setDailyCheckDepositLimit}
									monthlyCheckDepositLimit={monthlyCheckDepositLimit}
									setMonthlyCheckDepositLimit={setMonthlyCheckDepositLimit}
									dailyCheckDepositSoftLimit={dailyCheckDepositSoftLimit}
									setDailyCheckDepositSoftLimit={setDailyCheckDepositSoftLimit}
									monthlyCheckDepositSoftLimit={monthlyCheckDepositSoftLimit}
									setMonthlyCheckDepositSoftLimit={setMonthlyCheckDepositSoftLimit}
									isAutoCloseEnabled={isAutoCloseEnabled as boolean}
									setIsAutoCloseEnabled={setIsAutoCloseEnabled as React.Dispatch<React.SetStateAction<boolean>>}
									autoCloseAfter={autoCloseAfter as number}
									setAutoCloseAfter={setAutoCloseAfter as React.Dispatch<React.SetStateAction<number>>}
									enableOverdraft={enableOverdraft as boolean}
									setEnableOverdraft={setEnableOverdraft as React.Dispatch<React.SetStateAction<boolean>>}
									overdraftDaysLimit={overdraftDaysLimit}
									setOverdraftDaysLimit={setOverdraftDaysLimit}
									dailyWireLimit={dailyWireLimit}
									setDailyWireLimit={setDailyWireLimit}
									monthlyWireLimit={monthlyWireLimit}
									setMonthlyWireLimit={setMonthlyWireLimit}
									dailyWireSoftLimit={dailyWireSoftLimit}
									setDailyWireSoftLimit={setDailyWireSoftLimit}
									monthlyWireSoftLimit={monthlyWireSoftLimit}
									setMonthlyWireSoftLimit={setMonthlyWireSoftLimit}
									dailyCheckPaymentLimit={dailyCheckPaymentLimit}
									setDailyCheckPaymentLimit={setDailyCheckPaymentLimit}
									monthlyCheckPaymentLimit={monthlyCheckPaymentLimit}
									setMonthlyCheckPaymentLimit={setMonthlyCheckPaymentLimit}
									dailyCheckPaymentSoftLimit={dailyCheckPaymentSoftLimit}
									setDailyCheckPaymentSoftLimit={setDailyCheckPaymentSoftLimit}
									monthlyCheckPaymentSoftLimit={monthlyCheckPaymentSoftLimit}
									setMonthlyCheckPaymentSoftLimit={setMonthlyCheckPaymentSoftLimit}
									checkWritingEnabled={checkWritingEnabled as boolean}
									setCheckWritingEnabled={setCheckWritingEnabled as React.Dispatch<React.SetStateAction<boolean>>}
									isOverrideInterestSettingsId={isOverrideInterestSettingsId}
									setIsOverrideInterestSettingsId={setIsOverrideInterestSettingsId}
									interestSettingsId={interestSettingsId}
									setInterestSettingsId={setInterestSettingsId}
									enableCheckDepositDynamicClearingDays={enableCheckDepositDynamicClearingDays}
									setEnableCheckDepositDynamicClearingDays={setEnableCheckDepositDynamicClearingDays}
									enableDynamicClearingDaysAchDebit={enableDynamicClearingDaysAchDebit}
									setEnableDynamicClearingDaysAchDebit={setEnableDynamicClearingDaysAchDebit}
									achDebitMinClearingDays={achDebitMinClearingDays}
									setAchDebitMinClearingDays={setAchDebitMinClearingDays}
									achDebitMaxClearingDays={achDebitMaxClearingDays}
									setAchDebitMaxClearingDays={setAchDebitMaxClearingDays}
									isAchDebitFraudPredictEnabled={isAchDebitFraudPredictEnabled}
									setIsAchDebitFraudPredictEnabled={setIsAchDebitFraudPredictEnabled}
									isSweepEnabled={isSweepEnabled}
									setIsSweepEnabled={setIsSweepEnabled}
									checkWritingOriginationEnabled={checkWritingOriginationEnabled as boolean}
									setCheckWritingOriginationEnabled={
										setCheckWritingOriginationEnabled as React.Dispatch<React.SetStateAction<boolean>>
									}
									realTimePaymentsEnabled={realTimePaymentsEnabled}
									setRealTimePaymentsEnabled={setRealTimePaymentsEnabled}
									orgInternationalServiceFee={orgInternationalServiceFee}
									setOrgInternationalServiceFee={setOrgInternationalServiceFee}
									additionalVerificationThreshold={additionalVerificationThreshold}
									setAdditionalVerificationThreshold={setAdditionalVerificationThreshold}
									enableCustomerInterestFormula={enableCustomerInterestFormula}
									setEnableCustomerInterestFormula={setEnableCustomerInterestFormula}
									customerInterestFormula={customerInterestFormula}
									setCustomerInterestFormula={setCustomerInterestFormula}
									interestFormula={interestTerms?.interestFormula}
									interestRate={interestTerms?.interestRate}
									orgGlId={orgGlId}
									setOrgGlId={setOrgGlId}
									orgFboId={orgFboId}
									setOrgFboId={setOrgFboId}
									minimumIdentityVerificationLevel={minimumIdentityVerificationLevel}
									setMinimumIdentityVerificationLevel={setMinimumIdentityVerificationLevel}
								/>
								{isEditing ? (
									<div className="columns">
										<div className="column">
											<div className="card">
												<Footer state={state} isDisabled={!validate()} />
											</div>
										</div>
									</div>
								) : null}
							</fieldset>
						</form>
					)}
				</div>

				<BodyPortal>
					<ReactTooltip
						arrowColor="#FFF"
						place="bottom"
						type="light"
						effect="solid"
						id={"disable-walletTerms"}
						className="account-button-tooltip"
					>
						<p> There are active accounts on this wallet terms, close all accounts first.</p>
					</ReactTooltip>

					<ReactTooltip
						arrowColor="#FFF"
						place="bottom"
						type="light"
						effect="solid"
						id={"cannot-edit-when-disabled"}
						className="account-button-tooltip"
					>
						<p>{` Editing isn't possible when Wallet Terms is disabled`}</p>
					</ReactTooltip>
				</BodyPortal>
			</MainSection>
		</>
	)
}

function WalletTermsThatCanEdit() {
	const isUnitUser = useIsUnitUser()
	const {walletTermsId = ""} = useParams<{walletTermsId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()

	function emptyAsyncResult<T>() {
		return Promise.resolve(ok([] as T[]))
	}

	const walletTerms = useAsyncResult(() => getWalletTerms(accessToken, walletTermsId), [refreshToken])
	const orgs = useAsyncResult(() => findOrgs(accessToken, 0, 10000))
	const orgGlAccounts = useAsyncResult(() => findActiveAccountsByType(accessToken, AccountType.orgGeneralLedger))
	const orgFboAccounts = useAsyncResult(() => findActiveAccountsByType(accessToken, AccountType.fbo))
	const banks = useAsyncResult(() => findBanks(accessToken, 0, 1000))
	const bankRoutings = useAsyncResult(() =>
		isUnitUser ? findBankRoutings(accessToken) : emptyAsyncResult<BankRouting>()
	)
	const transactionMonitoringSettings = useAsyncResult(() =>
		isUnitUser
			? findTransactionMonitoringSettings(accessToken, 0, 100)
			: emptyAsyncResult<TransactionMonitoringSettings>()
	)
	const bins = useAsyncResult(() =>
		isUnitUser ? findBins(accessToken, 0, 2000, undefined, undefined, undefined, "name") : Promise.resolve(ok(null))
	)
	const interestSettings = useAsyncResult(() => findInterestSettings(accessToken, 0, 1000))
	const associatedAccounts = useAsyncResult(() => findActiveAssociatedAccounts(accessToken, parseInt(walletTermsId)))

	return AsyncResult.zip7(
		walletTerms,
		orgs,
		AsyncResult.zip(banks, bankRoutings),
		transactionMonitoringSettings,
		bins,
		AsyncResult.zip(associatedAccounts, interestSettings),
		AsyncResult.zip(orgGlAccounts, orgFboAccounts)
	).match(
		() => <Loading />,
		([
			walletTerms,
			orgs,
			[banks, bankRoutings],
			transactionMonitoringSettings,
			bins,
			[associatedAccounts, interestSettings],
			[orgGlAccounts, orgFboAccounts],
		]) => {
			const binsData = bins?.data ?? []
			return (
				<Page
					walletTerms={walletTerms}
					banks={banks}
					bankRoutings={bankRoutings}
					orgs={orgs}
					orgGlAccounts={orgGlAccounts.data}
					orgFboAccounts={orgFboAccounts.data}
					refresh={refresh}
					transactionMonitoringSettings={transactionMonitoringSettings}
					bins={binsData}
					hasAssociatedAccounts={associatedAccounts.data.length > 0}
					interestSettings={interestSettings.data}
				/>
			)
		},
		(_) => null
	)
}

function WalletTermsViewOnly() {
	const accessToken = useAccessToken()
	const {walletTermsId = ""} = useParams<{walletTermsId: string}>()
	const [refreshToken, refresh] = useRefreshToken()
	const walletTerms = useAsyncResult(() => getWalletTerms(accessToken, walletTermsId), [refreshToken])

	return walletTerms.match(
		() => <Loading />,
		(wt) => (
			<Page
				walletTerms={wt}
				banks={[]}
				bankRoutings={[]}
				orgs={[]}
				orgGlAccounts={[]}
				orgFboAccounts={[]}
				refresh={refresh}
				transactionMonitoringSettings={[]}
				bins={[]}
				hasAssociatedAccounts={false}
				interestSettings={[]}
			/>
		),
		(_) => null
	)
}

export function isDisabled(walletTerms: WalletTerms) {
	return walletTerms.attributes.status === "Disabled"
}

export default function WalletTermsPage() {
	const isUnitUser = useIsUnitUser()
	const isBankOperationsUser = useIsBankOperationsUser()

	return isUnitUser || isBankOperationsUser ? <WalletTermsThatCanEdit /> : <WalletTermsViewOnly />
}
